<template>
  <!-- 2026-09-22：所有管理功能共用顶部蓝色标题说明，正文不再重复占用大块介绍区域。 -->
  <div class="function-navigation">
    <button class="back-icon" type="button" aria-label="返回上一页" title="返回上一页" @click="goback">
      <svg-icon icon="prev" aria-hidden="true"></svg-icon>
    </button>
    <div class="function-navigation-content">
      <div class="function-heading">
        <div class="function-heading-copy">
        <h1 class="function-title">{{ functionTitle }}</h1>
        <p class="function-description">{{ functionDescription }}
          <span class="function-help-trigger">
            <button class="function-video-button" type="button" aria-label="打开功能帮助" title="打开功能帮助" @click="guideHelpVisible = true">帮助</button>
            <button class="function-help-question" type="button" aria-label="打开功能帮助" title="打开功能帮助" @click="guideHelpVisible = true">?</button>
          </span>
        </p>
        </div>
        <span class="function-heading-tail" aria-hidden="true"></span>
      </div>
      <!-- 2026-09-22：续费提醒独立保留红字、无背景及单行展示，不混入蓝色功能说明。 -->
      <div class="membership-reminder" v-if="reminderText" role="status">{{ reminderText }}</div>
    </div>
  </div>
  <FunctionHelp :visible="guideHelpVisible" :url="functionVideoUrl" :title="functionTitle" :description="functionDescription" :path="route.path" @close="guideHelpVisible = false" />
</template>
<script lang="ts">
import { useRoute, RouteLocationMatched } from 'vue-router'
import { watch, ref, computed, defineComponent, onMounted, onBeforeUnmount } from 'vue'
import { generateTitle as translateTitle } from '@/utils/lang'
// 2026-09-17：平台页面面包屑使用新栏目名称，分店仍保留原名称。
import { administrationTitleKey } from '@/utils/platformAdministration'
import { isNull } from '@/utils/route'
import sysRequest from '@/api/sys'
import { branchMembershipReminder, selectBranchMembership, BranchMembershipInfo } from '@/utils/branchMembershipReminder'
import store from '@/store'
import { resolveAdminFunctionDescription } from '@/utils/adminFunctionDescriptions'
// 2026-09-22：顶部采用平台保存的用途/视频，空配置继续使用原平台或分店用途说明。
import { findFunctionGuide } from '@/utils/functionGuides'
import { functionGuides, refreshFunctionGuides, resetFunctionGuides } from '@/utils/functionGuideState'
import FunctionHelp from '@/components/FunctionHelp.vue'
export default defineComponent({
components: { FunctionHelp },
setup(){
  const generateTitle=(title)=>translateTitle(administrationTitleKey(title,store.getters.userInfo.orgId))
  const breadPath = ref<RouteLocationMatched[]>([])
  const route = useRoute()
  breadPath.value = route.matched.filter((r) => !isNull(r.meta))

  // 2026-09-22：标题跟随当前最深层路由，动态菜单名称兜底，平台/分店沿用各自栏目命名。
  const functionTitle = computed(() => {
    const leaf = breadPath.value[breadPath.value.length - 1]
    const menus = store.getters.functions?.allFunc || []
    const menu = menus.find(item => String(item.functionUrl || '').split(/[?#]/, 1)[0].toLowerCase() === route.path.toLowerCase())
    return translateTitle(administrationTitleKey(leaf?.meta.title || menu?.functionKey || '', store.getters.userInfo?.orgId), menu?.functionName) || '工作台'
  })
  const activeGuide = computed(() => findFunctionGuide(functionGuides.value, route.path))
  const functionDescription = computed(() => activeGuide.value?.description || resolveAdminFunctionDescription(route.path, functionTitle.value, Number(store.getters.userInfo?.orgId)))
  const functionVideoUrl = computed(() => activeGuide.value?.videoUrl || '')
  // 2026-10-15：帮助可先于视频配置打开；配置加载只更新内容，路由或账号变化时再关闭。
  const guideHelpVisible = ref(false)
  const currentPath = ref('')
  const goback = () => {
    window.history.back()
  }
  // 2026-09-22：返回管理端/切换页面及可见时每分钟刷新；清理旧账号、旧请求和定时器，避免续费后残留提示。
  const memberInfo = ref<BranchMembershipInfo>()
  const currentTime = ref(Date.now())
  const sessionKey = computed(() => JSON.stringify([
    store.getters.token, store.getters.userInfo?.orgId, store.getters.userInfo?.id, store.getters.userInfo?.memberCustomerId
  ]))
  const reminderText = computed(() => branchMembershipReminder(memberInfo.value, currentTime.value))
  let mounted = false
  let requestVersion = 0
  let pendingVersion: number | undefined
  let lastRefreshed = 0
  let refreshTimer: number | undefined
  const refreshMembership = async (force = false) => {
    currentTime.value = Date.now()
    const orgId = Number(store.getters.userInfo?.orgId)
    if (!mounted || document.hidden || !store.getters.token || !(orgId > 1000)) return
    if (pendingVersion !== undefined || (!force && currentTime.value - lastRefreshed < 30000)) return
    const key = sessionKey.value
    const version = ++requestVersion
    pendingVersion = version
    try {
      const res = await sysRequest.getMemberInfo(orgId)
      if (mounted && version === requestVersion && key === sessionKey.value) {
        memberInfo.value = selectBranchMembership(Array.isArray(res.data) ? res.data : [], store.getters.userInfo?.memberCustomerId)
        lastRefreshed = Date.now()
      }
    } catch {
      // 短暂网络失败保留上次已确认状态，下一次刷新自动重试，不弹出重复错误。
    } finally {
      if (pendingVersion === version) pendingVersion = undefined
    }
  }
  const guideSession = () => store.getters.token ? sessionKey.value : ''
  const refreshGuides = () => {
    if (mounted && !document.hidden) void refreshFunctionGuides(guideSession())
  }
  const refreshOnReturn = () => { void refreshMembership(true); refreshGuides() }
  watch(sessionKey, () => {
    guideHelpVisible.value = false
    resetFunctionGuides(guideSession())
    refreshGuides()
    memberInfo.value = undefined
    lastRefreshed = 0
    requestVersion++
    pendingVersion = undefined
    void refreshMembership(true)
  })
  watch(route, () => {
    guideHelpVisible.value = false
    refreshGuides()
    breadPath.value = route.matched.filter((r) => !isNull(r.meta))
    currentPath.value = route.path
    void refreshMembership()
  })
  onMounted(() => {
    mounted = true
    refreshOnReturn()
    window.addEventListener('focus', refreshOnReturn)
    document.addEventListener('visibilitychange', refreshOnReturn)
    refreshTimer = window.setInterval(() => { void refreshMembership() }, 60000)
  })
  onBeforeUnmount(() => {
    mounted = false
    requestVersion++
    window.clearInterval(refreshTimer)
    window.removeEventListener('focus', refreshOnReturn)
    document.removeEventListener('visibilitychange', refreshOnReturn)
  })
  return {
    generateTitle,currentPath,breadPath,goback,reminderText,functionTitle,functionDescription,functionVideoUrl,guideHelpVisible,route
  }
}
})

</script>
<style lang="scss" scoped>
// 2026-09-22：标题和用途并排显示，说明可完整换行；顶部随内容增高，窄屏不挤占右侧操作。
.function-navigation { display:flex; align-items:center; flex:1 1 0; min-width:0; gap:6px; }
.back-icon {
  display:flex; align-items:center; justify-content:center; flex:0 0 24px; width:24px; height:34px;
  padding:0; border:0; border-radius:4px; background:transparent; color:#8290a0; cursor:pointer;
  &:hover { background:#edf3f9; color:#0756a3; }
  &:focus-visible { outline:2px solid var(--activeColor,#1890ff); outline-offset:2px; }
}
.function-navigation-content { flex:1 1 0; min-width:0; }
// 文字与渐变分区：文字区保持主色，尾部独立淡至顶部实际底色。
.function-heading {
  display:flex;align-items:stretch;width:85%;min-width:min(100%,560px);max-width:100%;min-height:40px;
  box-sizing:border-box;color:#fff;
  border-radius:6px 0 0 6px;
  background:linear-gradient(90deg,var(--activeColor,#1890ff) 0%,var(--activeColor,#1890ff) 50%,var(--admin-header-background,#fff) 100%);
}
.function-heading-copy {
  display:flex;align-items:center;gap:12px;flex:1 1 360px;min-width:0;padding:6px 12px;
  background:transparent;
}
.function-heading-tail {
  display:block;flex:0 1 clamp(200px,30%,320px);min-width:40px;align-self:stretch;pointer-events:none;
  background:transparent;
}
// 2026-10-25：标题18px、说明12px，保留正常字重与完整内容。
.function-title { flex:0 1 auto; min-width:0; max-width:50%; margin:0; color:inherit; font-size:18px; font-weight:400; line-height:1.35; white-space:normal; overflow-wrap:anywhere; }
.function-description { flex:1 1 180px; min-width:0; margin:0; font-size:12px; font-weight:400; line-height:1.5; color:#f0f7ff; overflow-wrap:anywhere; }
/* 2026-10-22：帮助胶囊缩为36×20px、12px正常字重；问号保留独立入口。 */
.function-help-trigger { display:inline-flex; vertical-align:middle; align-items:center; gap:5px; margin-left:8px; white-space:nowrap; }
.function-video-button { display:inline-flex; align-items:center; justify-content:center; box-sizing:border-box; width:36px; height:20px; padding:0; border:1px solid #fff; border-radius:18px; background:#fff; color:var(--activeColor,#1677ff); font-size:12px; font-weight:400; line-height:1.2; white-space:nowrap; box-shadow:0 2px 7px rgba(0,38,87,.18); cursor:pointer; }
.function-video-button:hover { background:var(--admin-skin-soft,#eaf4ff); box-shadow:0 3px 10px rgba(0,38,87,.24); }
.function-video-button:focus-visible { outline:2px solid white; outline-offset:2px; }
.function-help-question { display:inline-flex; align-items:center; justify-content:center; box-sizing:border-box; width:18px; height:18px; padding:0; border:1px solid #fff; border-radius:50%; background:transparent; color:#fff; font-size:12px; font-weight:700; line-height:1; cursor:pointer; }
.function-help-question:hover { background:#ffffff1a; }
.function-help-question:focus-visible { outline:2px solid white; outline-offset:2px; }
.membership-reminder {
  max-width:100%; min-width:0; margin-top:4px; padding:0; overflow-x:auto; white-space:nowrap;
  background:transparent; font-size:12px; color:red;
}
@media(max-width:1100px) {
  .function-heading-copy { gap:10px; padding:6px 10px; }
}
@media(max-width:600px) {
  .function-heading { width:100%; }
  .function-heading-copy { flex-wrap:wrap; gap:4px 10px; }
  .function-heading-tail { flex-basis:15%; min-width:24px; }
  .function-title { max-width:100%; }
  .function-description { flex-basis:100%; }
}
</style>
